/*
 * AI 填表员首页 · 新用户版(HomeV2)
 *
 * A/B 实验 `AI_form_filling_master_abtest_v2` 命中时展示此版本(见 aiClerk/index.tsx)。
 * 按用户设计稿(紫色渐变 Hero + 浮动 demo 文档 + overlap 上传卡 + 工程表卡片网格 +
 * 三步流程)实现。所有入口(上传卡 / 表单卡 / "没有你的表")统一走「上传文档」链路:
 *   选文件 → StartModal(可加传样例) → 上传 + diff → 跳 work 页。
 *
 * 上传 / 积分拦截 / StartModal 逻辑沿用旧 Home,与旧 Home 保持一致。
 */

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { message, Button, Modal } from 'antd'
import {
  ArrowUpOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  FileExcelFilled,
  FileFilled,
  FilePdfFilled,
  FilePptFilled,
  FileTextFilled,
  FileWordFilled,
  LoadingOutlined,
  SwapOutlined,
  ThunderboltFilled,
  WarningOutlined
} from '@ant-design/icons'
import { xheyAi, SINGLE_QUERY_COST } from '../api'
import type { CreditsBalance } from '../api'
import { useCurrentFile } from '../ctx/FileCtx'
import {
  computeExcelDiff,
  diffToPromptText,
  readFileAsArrayBuffer,
  probeTemplateSampleMatch,
  MatchProbeResult
} from '../utils/excelDiff'
import { ensureSafeFile } from '../utils/sanitizeFileName'
import {
  OFFICE_ACCEPT_TYPES as ACCEPT_TYPES,
  validateOfficeFile
} from '../utils/officeFileTypes'
import {
  trackEnter,
  trackClick,
  trackResult,
  trackDialogShow,
  trackDialogClick
} from '../utils/track'
import { useCredits } from '../ctx/CreditsCtx'
import { usePayment, useVipKind } from '../../../app/hooks'
import { useGroupPhotoNumFormated } from '../../../app/hooks/useGroupPhotoNumFormated'
import CreditsExhaustedModal from '../components/CreditsExhaustedModal'
import PointsPackModal from '../../components/PointsPackModal'
import { aiClerkPayContent } from '../utils/paymentContent'
import DemoReplayModal from '../demo/DemoReplayModal'
import { EditSkillModal } from '../components/EditSkillModal'
import CONFIG from '../../../constants/config'
import type { SkillItem, FileItem } from '../types'
// sessionStorage key 与 SamplePayload 复用旧 Home 的定义(同一份,避免 key 漂移导致
// work 页消费不到)
import { SAMPLE_DIFF_PROMPT_KEY, DESCRIBE_PROMPT_KEY } from './index'
import type { SamplePayload } from './index'
import './index.scss'
import './HomeV2.scss'

function formatBytes(n?: number): string {
  if (!n || n < 0) return ''
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`
}

function formatSessionTime(input: string | number | undefined): string {
  if (!input) return ''
  const ts = typeof input === 'number' ? input : new Date(input).getTime()
  if (Number.isNaN(ts)) return ''
  const diff = Date.now() - ts
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return '刚刚'
  if (mins < 60) return `${mins} 分钟前`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours} 小时前`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days} 天前`
  const d = new Date(ts)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

// 文件类型 → antd6 实心图标查表（pickFileIconStyle 返回的 type 字符串）
const FILE_FILLED_ICONS: Record<string, React.ComponentType<any>> = {
  'file-excel': FileExcelFilled,
  'file-word': FileWordFilled,
  'file-ppt': FilePptFilled,
  'file-pdf': FilePdfFilled,
  'file-text': FileTextFilled,
  file: FileFilled
}
const FileTypeIcon = ({
  name,
  ...rest
}: {
  name: string
  [k: string]: any
}) => {
  const C = FILE_FILLED_ICONS[name]
  return C ? <C {...rest} /> : null
}

function pickFileIconStyle(filename: string): {
  type:
    | 'file-excel'
    | 'file-word'
    | 'file-pdf'
    | 'file-ppt'
    | 'file-text'
    | 'file'
  bg: string
  color: string
} {
  const ext = (filename.split('.').pop() || '').toLowerCase()
  if (ext === 'xlsx' || ext === 'xls' || ext === 'csv')
    return { type: 'file-excel', bg: '#E8F5E9', color: '#1B7C3E' }
  if (ext === 'docx' || ext === 'doc')
    return { type: 'file-word', bg: '#E3F2FD', color: '#1565C0' }
  if (ext === 'pdf')
    return { type: 'file-pdf', bg: '#FFEBEE', color: '#C62828' }
  if (ext === 'pptx' || ext === 'ppt')
    return { type: 'file-ppt', bg: '#FFF3E0', color: '#D84315' }
  if (ext === 'txt' || ext === 'md')
    return { type: 'file-text', bg: '#F5F5F5', color: '#616161' }
  return { type: 'file', bg: '#F5F5F5', color: '#757575' }
}

const XLSX_MEDIA_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
const XLS_MEDIA_TYPE = 'application/vnd.ms-excel'
const DOCX_MEDIA_TYPE =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
const DOC_MEDIA_TYPE = 'application/msword'

function pickMediaType(filename: string): string {
  const ext = (filename.split('.').pop() || '').toLowerCase()
  if (ext === 'xlsx') return XLSX_MEDIA_TYPE
  if (ext === 'xls') return XLS_MEDIA_TYPE
  if (ext === 'docx') return DOCX_MEDIA_TYPE
  if (ext === 'doc') return DOC_MEDIA_TYPE
  return 'application/octet-stream'
}

/** 「可填表单」橱窗的一张卡:字形图标 + 表名 + 一句话说明 */
interface FormCardV2 {
  icon: string
  name: string
  desc: string
}

const FORM_CARDS_V2: FormCardV2[] = [
  { icon: '▤', name: '施工验收台账表', desc: '按桩号 / 部位自动配检查照片' },
  { icon: '◷', name: '考勤签到表', desc: '每人每天打卡水印照自动归位' },
  { icon: '◎', name: '工地巡检报告', desc: '按时间地点排好巡检影像' },
  { icon: '⚠', name: '安全隐患排查表', desc: '隐患点照片自动对位填入' },
  { icon: '◉', name: '监理巡视影像表', desc: '巡视记录配对现场照片' },
  { icon: '▣', name: '设备进场验收表', desc: '设备铭牌 / 进场照自动填' },
  { icon: '✓', name: '文明施工检查表', desc: '各检查项配对现场照片' }
]

/** Hero 浮动 demo 文档里的 4 张照片格(纯装饰,样式占位 + 角标) */
const DEMO_CELLS = [
  { name: '1号靠船墩', time: '1-18 · 12:42' },
  { name: '1号靠船墩', time: '1-17 · 13:04' },
  { name: '1号靠船墩', time: '1-16 · 13:20' },
  { name: '1号靠船墩', time: '1-15 · 13:38' }
]

/** 「填好只要三步」 */
const STEPS_V2 = [
  {
    title: '解析文档结构',
    desc: '自动读懂表格，定位每个该填图的空格。担心识别不准？可同时上传空表 + 填好的样本。'
  },
  {
    title: '智能识别内容',
    desc: '看懂每格要什么图，不确定时弹窗与你确认数据范围，按约束条件更精准地找图。'
  },
  {
    title: '自动填入照片',
    desc: '从团队照片库里挑对的照片，一张张填好，可一键导出成稿。'
  }
]

const HomeV2: React.FC = () => {
  const navigate = useNavigate()
  const { setFile } = useCurrentFile()
  const { credits, refresh: refreshCredits } = useCredits()
  const payment = usePayment()
  const vipKind = useVipKind()
  const [creditsModalVisible, setCreditsModalVisible] = useState(false)
  const [pointsPackVisible, setPointsPackVisible] = useState(false)
  const [demoVisible, setDemoVisible] = useState(false)
  const [genText, setGenText] = useState('')
  const [skills, setSkills] = useState<SkillItem[]>([])
  const [skillsLoading, setSkillsLoading] = useState(true)
  const [editingSkill, setEditingSkill] = useState<SkillItem | null>(null)
  const [recentFiles, setRecentFiles] = useState<FileItem[]>([])
  const [recentFilesLoading, setRecentFilesLoading] = useState(true)
  // 被余额拦截的发起动作;续费成功后自动重试
  const pendingStartRef = useRef<null | 'upload' | SkillItem>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const sampleInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [dragOverSample, setDragOverSample] = useState(false)
  /** 用户选好的模板——延迟到点"开始识别"再上传,让用户有机会先添加样例 */
  const [templateFile, setTemplateFile] = useState<File | null>(null)
  /** 用户预先选好的"参考样例"——只存 File,不上传,diff 算完即抛 */
  const [sampleFile, setSampleFile] = useState<File | null>(null)
  const [probeResult, setProbeResult] = useState<MatchProbeResult | null>(null)
  const [probing, setProbing] = useState(false)
  const [startModalOpen, setStartModalOpen] = useState(false)
  // 团队照片总数(hero 数字),与顶栏 Header 统计同源同算法;未加载时为 null
  const photoNum = useGroupPhotoNumFormated()

  const loadSkills = useCallback(async () => {
    setSkillsLoading(true)
    try {
      const list = await xheyAi.listUserSkills()
      setSkills(
        [...list].sort((a, b) => (b.updated_at || 0) - (a.updated_at || 0))
      )
    } catch {
      setSkills([])
    } finally {
      setSkillsLoading(false)
    }
  }, [])

  const loadRecentFiles = useCallback(async () => {
    setRecentFilesLoading(true)
    try {
      const list = await xheyAi.listRecentFiles({ limit: 10 })
      setRecentFiles(
        [...list].sort((a, b) => (b.mtime_ms || 0) - (a.mtime_ms || 0))
      )
    } catch {
      setRecentFiles([])
    } finally {
      setRecentFilesLoading(false)
    }
  }, [])

  useEffect(() => {
    loadSkills()
    loadRecentFiles()
  }, [loadSkills, loadRecentFiles])

  // 页面进入埋点(只 mount 时上报一次)
  useEffect(() => {
    trackEnter('formFiller', {
      requirement_id: 1103,
      track_sign: 'aai.b8134.pageview.ai_enter_web_ai_form_filler'
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 上传文档半屏进入埋点(Modal false → true 时上报一次)
  const startModalOpenRef = useRef(false)
  useEffect(() => {
    if (startModalOpen && !startModalOpenRef.current) {
      trackEnter('uploadDoc', {
        requirement_id: 1103,
        track_sign: 'aai.b8135.pageview.ai_enter_web_ai_upload_doc',
        enterFrom: 'web_ai_form_filler'
      })
    }
    startModalOpenRef.current = startModalOpen
  }, [startModalOpen])

  // 预匹配探测:模板 + 样例都是 xlsx 时跑一次轻量字段对齐检测。debounce 300ms。
  useEffect(() => {
    if (!templateFile || !sampleFile) {
      setProbeResult(null)
      setProbing(false)
      return
    }
    const tExt = (templateFile.name.split('.').pop() || '').toLowerCase()
    const sExt = (sampleFile.name.split('.').pop() || '').toLowerCase()
    const isExcelPair =
      ['xlsx', 'xls'].includes(tExt) && ['xlsx', 'xls'].includes(sExt)
    if (!isExcelPair) {
      setProbeResult(null)
      setProbing(false)
      return
    }
    let cancelled = false
    const timer = setTimeout(async () => {
      if (cancelled) return
      setProbing(true)
      try {
        const [tBuf, sBuf] = await Promise.all([
          readFileAsArrayBuffer(templateFile),
          readFileAsArrayBuffer(sampleFile)
        ])
        if (cancelled) return
        const result = await probeTemplateSampleMatch(tBuf, sBuf)
        if (cancelled) return
        setProbeResult(result)
      } catch (e) {
        if (cancelled) return
        // eslint-disable-next-line no-console
        console.warn('[aiClerk] 预匹配探测失败:', e)
        setProbeResult({
          matchPercent: 0,
          matchedHeaders: 0,
          totalHeaders: 0,
          reversed: false,
          templateFilledCount: 0,
          sampleFilledCount: 0,
          parseError: e instanceof Error ? e.message : String(e)
        })
      } finally {
        if (!cancelled) setProbing(false)
      }
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [templateFile, sampleFile])

  const handleSwapTemplateAndSample = useCallback(() => {
    setTemplateFile((prev) => {
      const wasSample = sampleFile
      setSampleFile(prev)
      return wasSample
    })
  }, [sampleFile])

  const handlePickTemplate = useCallback(() => {
    if (startModalOpen) {
      trackClick('uploadDoc', 'upload_area', {
        requirement_id: 1103,
        track_sign: 'aai.b8135.click.ai_click_web_ai_upload_doc'
      })
    } else {
      trackClick('formFiller', 'upload_area', {
        requirement_id: 1103,
        track_sign: 'aai.b8134.click.ai_click_web_ai_form_filler'
      })
    }
    if (fileInputRef.current) fileInputRef.current.click()
  }, [startModalOpen])

  const handleClearTemplateInModal = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (uploading) return
      trackClick('uploadDoc', 'remove_file', {
        requirement_id: 1103,
        track_sign: 'aai.b8135.click.ai_click_web_ai_upload_doc'
      })
      setTemplateFile(null)
    },
    [uploading]
  )

  const handleTemplateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      e.target.value = ''
      if (!file) return
      const err = validateOfficeFile(file)
      if (err) {
        message.error(err)
        return
      }
      setTemplateFile(file)
      setStartModalOpen(true)
    },
    []
  )

  const handleStartModalClose = useCallback(() => {
    if (uploading) return
    trackClick('uploadDoc', 'close', {
      requirement_id: 1103,
      track_sign: 'aai.b8135.click.ai_click_web_ai_upload_doc'
    })
    setStartModalOpen(false)
    setTemplateFile(null)
    setSampleFile(null)
  }, [uploading])

  const handlePickSample = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    trackClick('uploadDoc', 'upload_sample', {
      requirement_id: 1103,
      track_sign: 'aai.b8135.click.ai_click_web_ai_upload_doc'
    })
    if (sampleInputRef.current) sampleInputRef.current.click()
  }, [])

  const handleSampleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      e.target.value = ''
      if (!file) return
      const err = validateOfficeFile(file)
      if (err) {
        message.error(`参考样例：${err}`)
        return
      }
      setSampleFile(file)
    },
    []
  )

  const handleSampleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOverSample(true)
  }, [])
  const handleSampleDragLeave = useCallback(() => {
    setDragOverSample(false)
  }, [])
  const handleSampleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault()
    setDragOverSample(false)
    const file = e.dataTransfer.files?.[0]
    if (!file) return
    const err = validateOfficeFile(file)
    if (err) {
      message.error(`参考样例：${err}`)
      return
    }
    setSampleFile(file)
  }, [])

  const handleTemplateDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault()
    const file = e.dataTransfer.files?.[0]
    if (!file) return
    const err = validateOfficeFile(file)
    if (err) {
      message.error(err)
      return
    }
    setTemplateFile(file)
    setStartModalOpen(true)
  }, [])

  /**
   * 用户点击发起会话前:确保 credits 已就绪(瞬态故障时主动补 refresh 一次)。
   *   - { ok:true, credits }:有效余额
   *   - { ok:false }:已 message.error「网络错误，请刷新重试」,调用方 return
   */
  const ensureCreditsReady = useCallback(async (): Promise<{
    ok: boolean
    credits: CreditsBalance | null
  }> => {
    let c: CreditsBalance | null = credits
    if (!c || c.available == null) {
      c = await refreshCredits()
    }
    if (!c || c.available == null) {
      message.error('网络错误，请刷新重试')
      return { ok: false, credits: null }
    }
    return { ok: true, credits: c }
  }, [credits, refreshCredits])

  // 真正的上传 + diff + 跳转——点"开始识别"才触发
  const handleStart = useCallback(
    async (skipGuard = false) => {
      if (uploading) return
      if (!skipGuard) {
        const ready = await ensureCreditsReady()
        if (!ready.ok) return
        if ((ready.credits?.available ?? 0) <= 0) {
          pendingStartRef.current = 'upload'
          setCreditsModalVisible(true)
          return
        }
      }
      if (!templateFile) {
        message.error('请先选择要填写的模板')
        return
      }
      trackClick('uploadDoc', 'start_recognition', {
        requirement_id: 1103,
        track_sign: 'aai.b8135.click.ai_click_web_ai_upload_doc'
      })
      const tExt = (templateFile.name.split('.').pop() || '').toLowerCase()
      const sExt = sampleFile
        ? (sampleFile.name.split('.').pop() || '').toLowerCase()
        : ''
      const isExcelPair =
        ['xlsx', 'xls'].includes(tExt) && ['xlsx', 'xls'].includes(sExt)
      const isWordPair =
        ['docx', 'doc'].includes(tExt) && ['docx', 'doc'].includes(sExt)
      const sampleUsable = !!sampleFile && (isExcelPair || isWordPair)
      if (sampleFile && !sampleUsable) {
        message.warning(
          '参考样例必须与模板同类型（Excel↔Excel / Word↔Word），本次忽略样例继续上传'
        )
      }
      setUploading(true)
      try {
        const safeTemplate = ensureSafeFile(templateFile)
        const safeSample =
          sampleUsable && sampleFile ? ensureSafeFile(sampleFile) : null
        const t0 = Date.now()
        let templateCostTime = 0
        let sampleCostTime = 0
        const [uploadResult, sampleUploadResult, templateBuf, sampleBuf] =
          await Promise.all([
            xheyAi.upload(safeTemplate).then((r) => {
              templateCostTime = Date.now() - t0
              return r
            }),
            safeSample
              ? xheyAi.uploadAttachment(safeSample).then((r) => {
                  sampleCostTime = Date.now() - t0
                  return r
                })
              : Promise.resolve(null),
            isExcelPair
              ? readFileAsArrayBuffer(templateFile)
              : Promise.resolve(null),
            isExcelPair && sampleFile
              ? readFileAsArrayBuffer(sampleFile)
              : Promise.resolve(null)
          ])
        const tFmt = tExt === 'docx' || tExt === 'xlsx' ? tExt : 'other'
        trackResult('uploadDoc', 'upload_doc', {
          requirement_id: 1103,
          track_sign: 'aai.b8135.click.ai_result_web_ai_upload_doc',
          actionResult: 'success',
          string1: tFmt,
          number1: Math.round(safeTemplate.size / 1024),
          bool1: !!sampleUploadResult,
          costTime: templateCostTime
        })
        if (safeSample && sampleUploadResult) {
          const sFmt = sExt === 'docx' || sExt === 'xlsx' ? sExt : 'other'
          trackResult('uploadDoc', 'upload_sample', {
            requirement_id: 1103,
            track_sign: 'aai.b8135.click.ai_result_web_ai_upload_doc',
            actionResult: 'success',
            string1: sFmt,
            number1: Math.round(safeSample.size / 1024),
            costTime: sampleCostTime
          })
        }
        const { fileUrl, fileName, filePath, refreshUrl } = uploadResult
        if (sampleUsable && safeSample && sampleUploadResult) {
          let diffText = ''
          if (isExcelPair && templateBuf && sampleBuf) {
            try {
              const diff = await computeExcelDiff(
                templateBuf,
                sampleBuf,
                safeTemplate.name,
                safeSample.name
              )
              diffText = diffToPromptText(diff)
            } catch (e) {
              // eslint-disable-next-line no-console
              console.warn('[aiClerk] 样例 diff 计算失败:', e)
              const detail = e instanceof Error ? e.message : String(e)
              message.warning({
                content: `参考样例解析失败：${detail}（继续按附件方式发给 agent）`,
                duration: 6
              })
            }
          }
          const payload: SamplePayload = {
            diffText,
            attachment: {
              url: sampleUploadResult.fileUrl,
              name: safeSample.name,
              mediaType: pickMediaType(safeSample.name)
            }
          }
          window.sessionStorage.setItem(
            SAMPLE_DIFF_PROMPT_KEY,
            JSON.stringify(payload)
          )
        } else {
          window.sessionStorage.removeItem(SAMPLE_DIFF_PROMPT_KEY)
        }
        setFile({ fileUrl, fileName, filePath, refreshUrl })
        try {
          window.localStorage.removeItem('aiClerk:conversationId')
        } catch {
          /* 忽略 */
        }
        navigate('/ai/clerk/work')
      } catch (e) {
        const errMsg = e instanceof Error ? e.message : String(e)
        message.error(`上传失败：${errMsg}`)
        const tFmtRaw = (templateFile.name.split('.').pop() || '').toLowerCase()
        const tFmt =
          tFmtRaw === 'docx' || tFmtRaw === 'xlsx' ? tFmtRaw : 'other'
        trackResult('uploadDoc', 'upload_doc', {
          requirement_id: 1103,
          track_sign: 'aai.b8135.click.ai_result_web_ai_upload_doc',
          actionResult: 'fail',
          string1: tFmt,
          number1: Math.round(templateFile.size / 1024),
          bool1: !!sampleFile,
          errorMsg: errMsg
        })
        setUploading(false)
      }
    },
    [navigate, uploading, setFile, templateFile, sampleFile, ensureCreditsReady]
  )

  const startTemplate = useCallback(
    async (sk: SkillItem) => {
      trackClick('formFiller', 'continue_use_template', {
        requirement_id: 1103,
        track_sign: 'aai.b8134.click.ai_click_web_ai_form_filler',
        string1: sk.name
      })
      try {
        const result = await xheyAi.useSkill(sk.name)
        try {
          window.localStorage.removeItem('aiClerk:conversationId')
        } catch {
          /* 忽略 */
        }
        if (result.hasTemplate) {
          setFile({
            fileUrl: result.fileUrl,
            fileName: result.fileName,
            filePath: result.filePath,
            refreshUrl: result.refreshUrl
          })
        }
        const qs =
          `?skill=${encodeURIComponent(sk.name)}` +
          `&skillTitle=${encodeURIComponent(sk.display_name || sk.name)}`
        navigate(`/ai/clerk/work${qs}`)
      } catch (e) {
        message.error(
          `继续使用失败: ${e instanceof Error ? e.message : String(e)}`
        )
      }
    },
    [navigate, setFile]
  )

  // 续费成功后放行容错:续上被拦截的上传或模板发起
  const runPendingStart = useCallback(() => {
    const pending = pendingStartRef.current
    if (!pending) return
    pendingStartRef.current = null
    if (pending === 'upload') {
      handleStart(true)
    } else {
      startTemplate(pending)
    }
  }, [handleStart, startTemplate])

  // 会员升级成功(vipKind 变化)+ 有待发起动作 → 刷新余额 + 放行容错续会话
  const prevVipKindRef = useRef(vipKind)
  useEffect(() => {
    if (prevVipKindRef.current !== vipKind) {
      refreshCredits()
      if (pendingStartRef.current) {
        runPendingStart()
      }
    }
    prevVipKindRef.current = vipKind
  }, [vipKind, refreshCredits, runPendingStart])

  const handleGenerate = useCallback(async () => {
    if (uploading) return
    const text = genText.trim()
    if (!text) return
    // V2 特有"用一句话生成空白表"入口:点击 → 余额校验 → upload(空文档,只带描述) → 跳 work
    // number1 记 prompt 长度,便于看用户描述的信息密度
    trackClick('formFiller', 'generate_form', {
      requirement_id: 1103,
      track_sign: 'aai.b8134.click.ai_click_web_ai_form_filler',
      number1: text.length
    })
    const ready = await ensureCreditsReady()
    if (!ready.ok) return
    if ((ready.credits?.available ?? 0) <= 0) {
      pendingStartRef.current = 'upload'
      setCreditsModalVisible(true)
      return
    }
    setUploading(true)
    const t0 = Date.now()
    try {
      const { fileUrl, fileName, filePath, refreshUrl } = await xheyAi.upload()
      setFile({ fileUrl, fileName, filePath, refreshUrl })
      try {
        window.sessionStorage.setItem(DESCRIBE_PROMPT_KEY, text)
      } catch {
        /* 忽略 */
      }
      try {
        window.localStorage.removeItem('aiClerk:conversationId')
      } catch {
        /* 忽略 */
      }
      trackResult('formFiller', 'generate_form', {
        requirement_id: 1103,
        track_sign: 'aai.b8134.click.ai_result_web_ai_form_filler',
        actionResult: 'success',
        number1: text.length,
        costTime: Date.now() - t0
      })
      navigate('/ai/clerk/work')
    } catch (e) {
      const errMsg = e instanceof Error ? e.message : String(e)
      message.error(`生成失败：${errMsg}`)
      trackResult('formFiller', 'generate_form', {
        requirement_id: 1103,
        track_sign: 'aai.b8134.click.ai_result_web_ai_form_filler',
        actionResult: 'fail',
        number1: text.length,
        costTime: Date.now() - t0,
        errorMsg: errMsg
      })
      setUploading(false)
    }
  }, [uploading, genText, ensureCreditsReady, setFile, navigate])

  const handleWatchDemo = useCallback(() => {
    // 与 V1 旧 Home 对齐:action 用 'view_demo'(此前 V2 误写成 'watch_demo' 会让 BI
    // 报表把"看示例演示"拆成两组数据,统一改回 view_demo)
    trackClick('formFiller', 'view_demo', {
      requirement_id: 1103,
      track_sign: 'aai.b8134.click.ai_click_web_ai_form_filler'
    })
    setDemoVisible(true)
  }, [])

  // 两个接口都完成后才能判断展示哪套视图，加载期间两套都不渲染，避免先出新用户视图再切换
  const isLoading = skillsLoading || recentFilesLoading
  const hasHistory = !isLoading && (skills.length > 0 || recentFiles.length > 0)

  return (
    <div className="aiClerk-formV2">
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT_TYPES}
        hidden
        onChange={handleTemplateChange}
      />

      {/* ── Hero:紫色渐变带 + 左文案 + 右浮动 demo 文档 ── */}
      <section className="aiClerk-formV2__hero">
        <span className="aiClerk-formV2__hero-blob aiClerk-formV2__hero-blob--a" />
        <span className="aiClerk-formV2__hero-blob aiClerk-formV2__hero-blob--b" />
        <div className="aiClerk-formV2__hero-inner">
          <div className="aiClerk-formV2__hero-copy">
            <h1 className="aiClerk-formV2__hero-title">
              极速填表，就用AI填表员
            </h1>
            {!hasHistory && !isLoading && (
              <>
                <p className="aiClerk-formV2__hero-sub">
                  把 Word / Excel 交给 AI 填表员，3 分钟读懂表格结构，从团队{' '}
                  {photoNum
                    ? photoNum.unit === '万'
                      ? `${photoNum.value}万`
                      : photoNum.value
                    : '28 万'}
                  张照片里挑对的，自动填好——你只管核对。
                </p>
                <div className="aiClerk-formV2__hero-stats">
                  <div className="aiClerk-formV2__hero-stat">
                    <span className="aiClerk-formV2__hero-stat-num">
                      3 小时起 → 3 分钟
                    </span>
                    <span className="aiClerk-formV2__hero-stat-lab">
                      同样一份表，效率对比
                    </span>
                  </div>
                  <span className="aiClerk-formV2__hero-statline" />
                  <div className="aiClerk-formV2__hero-stat">
                    <span className="aiClerk-formV2__hero-stat-num">
                      {photoNum ? `${photoNum.value}${photoNum.unit}` : '—'}
                    </span>
                    <span className="aiClerk-formV2__hero-stat-lab">
                      团队照片库自动匹配
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
          {!hasHistory && !isLoading && (
            <button
              type="button"
              className="aiClerk-formV2__hero-demo"
              onClick={handleWatchDemo}
            >
              ▶ 看 3 分钟示例演示 · 不用上传
            </button>
          )}
        </div>
      </section>

      {/* ── 上传卡(overlap 在渐变带上,绝对主角) ── */}
      <div className="aiClerk-formV2__upwrap">
        <div
          className="aiClerk-formV2__upcard"
          onClick={handlePickTemplate}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleTemplateDrop}
        >
          <div className="aiClerk-formV2__upicon">
            <span className="aiClerk-formV2__upicon-ring" />
            <span className="aiClerk-formV2__upicon-core">⬆</span>
          </div>
          <div className="aiClerk-formV2__uptext">
            <div className="aiClerk-formV2__uptitle">
              上传一份要填的文档，马上开始
            </div>
            <div className="aiClerk-formV2__updesc">
              支持 .docx / .xlsx 拖进来或点击上传 · 可加传【样例】让识别更准
            </div>
          </div>
          <button
            type="button"
            className="aiClerk-formV2__upbtn"
            onClick={(e) => {
              e.stopPropagation()
              handlePickTemplate()
            }}
          >
            上传文档
          </button>
        </div>
        <div className="aiClerk-formV2__gen-hint">
          没有现成文档？
          <span className="aiClerk-formV2__gen-hint-link">
            ✨ 用一句话让 AI 生成空白表
          </span>
        </div>
        <div className="aiClerk-formV2__gen-bar">
          <input
            type="text"
            className="aiClerk-formV2__gen-bar-input"
            placeholder={`做一张 ${
              new Date().getMonth() + 1
            } 月施工验收台账，要包含照片、拍摄人、时间、地点……`}
            value={genText}
            onChange={(e) => setGenText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleGenerate()
            }}
            disabled={uploading}
          />
          <button
            type="button"
            className="aiClerk-formV2__gen-bar-btn"
            onClick={handleGenerate}
            disabled={uploading || !genText.trim()}
          >
            生成
          </button>
        </div>
      </div>

      {!isLoading && hasHistory ? (
        <div className="aiClerk-formV2__history">
          {/* ── 我的模板库 ── */}
          {skills.length > 0 && (
            <section className="aiClerk-formV2__sec">
              <h2 className="aiClerk-formV2__sec-title2">我的模板库</h2>
              <div className="aiClerk-formV2__templates">
                {skills.map((sk) => (
                  <div key={sk.name} className="aiClerk-formV2__template">
                    <div className="aiClerk-formV2__template-name">
                      {sk.display_name}
                    </div>
                    {(sk.summary || sk.description) && (
                      <div className="aiClerk-formV2__template-desc">
                        {sk.summary || sk.description}
                      </div>
                    )}
                    {sk.updated_at && (
                      <div className="aiClerk-formV2__template-meta">
                        {formatSessionTime(sk.updated_at)}
                      </div>
                    )}
                    <div className="aiClerk-formV2__template-actions">
                      <button
                        className="aiClerk-formV2__tbtn aiClerk-formV2__tbtn--primary"
                        onClick={async () => {
                          const ready = await ensureCreditsReady()
                          if (!ready.ok) return
                          if ((ready.credits?.available ?? 0) <= 0) {
                            pendingStartRef.current = sk
                            setCreditsModalVisible(true)
                            return
                          }
                          startTemplate(sk)
                        }}
                      >
                        继续使用
                      </button>
                      <button
                        className="aiClerk-formV2__tbtn aiClerk-formV2__tbtn--ghost"
                        onClick={() => {
                          trackClick('formFiller', 'edit_template', {
                            requirement_id: 1103,
                            track_sign:
                              'aai.b8134.click.ai_click_web_ai_form_filler',
                            string1: sk.name
                          })
                          setEditingSkill(sk)
                        }}
                      >
                        编辑
                      </button>
                      <button
                        className="aiClerk-formV2__tbtn aiClerk-formV2__tbtn--danger"
                        onClick={() => {
                          trackClick('formFiller', 'delete_template', {
                            requirement_id: 1103,
                            track_sign:
                              'aai.b8134.click.ai_click_web_ai_form_filler',
                            string1: sk.name
                          })
                          trackDialogShow(
                            'formFiller',
                            'confirm_delete_template',
                            {
                              requirement_id: 1103,
                              track_sign:
                                'aai.b8134.pageview.ai_dialog_show_web_ai_form_filler'
                            }
                          )
                          Modal.confirm({
                            title: `删除模板「${sk.display_name || sk.name}」?`,
                            content:
                              '删除后无法恢复，模板里的示例文件 / 业务描述都会一起删掉。',
                            okText: '删除',
                            okType: 'danger',
                            cancelText: '取消',
                            onOk: async () => {
                              trackDialogClick(
                                'formFiller',
                                'confirm_delete_template',
                                'confirm',
                                {
                                  requirement_id: 1103,
                                  track_sign:
                                    'aai.b8134.click.ai_dialog_click_web_ai_form_filler'
                                }
                              )
                              try {
                                await xheyAi.deleteUserSkill(sk.name)
                                message.success(
                                  `已删除「${sk.display_name || sk.name}」`
                                )
                                loadSkills()
                              } catch (e) {
                                message.error(
                                  `删除失败: ${
                                    e instanceof Error ? e.message : String(e)
                                  }`
                                )
                              }
                            },
                            onCancel: () => {
                              trackDialogClick(
                                'formFiller',
                                'confirm_delete_template',
                                'cancel',
                                {
                                  requirement_id: 1103,
                                  track_sign:
                                    'aai.b8134.click.ai_dialog_click_web_ai_form_filler'
                                }
                              )
                            }
                          })
                        }}
                      >
                        删除
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* ── 最近填好的文档 ── */}
          {recentFiles.length > 0 && (
            <section className="aiClerk-formV2__sec">
              <h2 className="aiClerk-formV2__sec-title2">最近填好的文档</h2>
              <div className="aiClerk-formV2__recent">
                {recentFiles.map((f) => {
                  const iconStyle = pickFileIconStyle(f.filename)
                  const inner = (
                    <>
                      <div
                        className="aiClerk-formV2__recent-icon"
                        style={{
                          background: iconStyle.bg,
                          color: iconStyle.color
                        }}
                      >
                        <FileTypeIcon name={iconStyle.type} />
                      </div>
                      <div className="aiClerk-formV2__recent-main">
                        <div
                          className="aiClerk-formV2__recent-name"
                          title={f.filename}
                        >
                          {f.filename}
                        </div>
                        <div className="aiClerk-formV2__recent-meta">
                          {[
                            formatSessionTime(f.mtime_ms),
                            formatBytes(f.size_bytes)
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </div>
                      </div>
                      <div className="aiClerk-formV2__recent-actions">
                        {f.download_url ? (
                          <span>下载</span>
                        ) : (
                          <span className="aiClerk-formV2__recent-disabled">
                            下载链接缺失
                          </span>
                        )}
                      </div>
                    </>
                  )
                  return f.download_url ? (
                    <a
                      key={f.path}
                      href={f.download_url}
                      download={f.filename}
                      className="aiClerk-formV2__recent-item"
                      onClick={() =>
                        trackClick('formFiller', 'download_doc', {
                          requirement_id: 1103,
                          track_sign:
                            'aai.b8134.click.ai_click_web_ai_form_filler',
                          string1: f.path
                        })
                      }
                    >
                      {inner}
                    </a>
                  ) : (
                    <div
                      key={f.path}
                      className="aiClerk-formV2__recent-item is-disabled"
                    >
                      {inner}
                    </div>
                  )
                })}
              </div>
            </section>
          )}
        </div>
      ) : !isLoading ? (
        <>
          {/* ── 可填表单清单 ── */}
          <section className="aiClerk-formV2__sec">
            <div className="aiClerk-formV2__sec-head">
              <div className="aiClerk-formV2__sec-title">
                <span className="aiClerk-formV2__sec-bar" />
                这些表，AI 都能帮你一张张填好
              </div>
              <span className="aiClerk-formV2__sec-note">
                已支持 200+ 种工程表格 · 选一类直接上传开填
              </span>
            </div>
            <div className="aiClerk-formV2__forms">
              {FORM_CARDS_V2.map((c) => (
                <button
                  key={c.name}
                  type="button"
                  className="aiClerk-formV2__formcard"
                  onClick={() => {
                    // string1 记被点的卡名,便于看不同模板品类的转化漏斗
                    trackClick('formFiller', 'form_card', {
                      requirement_id: 1103,
                      track_sign: 'aai.b8134.click.ai_click_web_ai_form_filler',
                      string1: c.name
                    })
                    handlePickTemplate()
                  }}
                  disabled={uploading}
                >
                  <div className="aiClerk-formV2__formcard-name">
                    {c.name}
                    <span className="aiClerk-formV2__formcard-up">上传 ↑</span>
                  </div>
                  <div className="aiClerk-formV2__formcard-desc">{c.desc}</div>
                </button>
              ))}
              <button
                type="button"
                className="aiClerk-formV2__formcard aiClerk-formV2__formcard--more"
                onClick={() => {
                  trackClick('formFiller', 'form_card', {
                    requirement_id: 1103,
                    track_sign: 'aai.b8134.click.ai_click_web_ai_form_filler',
                    string1: 'no_match'
                  })
                  handlePickTemplate()
                }}
                disabled={uploading}
              >
                <div className="aiClerk-formV2__formcard-name aiClerk-formV2__formcard-name--accent">
                  没有你的表？
                </div>
                <div className="aiClerk-formV2__formcard-desc">
                  直接把 Word / Excel 拖进来，AI 照样能填 →
                </div>
              </button>
            </div>
          </section>

          {/* ── 填好只要三步 ── */}
          <section className="aiClerk-formV2__sec aiClerk-formV2__sec--steps">
            <div className="aiClerk-formV2__sec-title">
              <span className="aiClerk-formV2__sec-bar" />
              填好只要三步，你只管核对
            </div>
            <div className="aiClerk-formV2__steps">
              {STEPS_V2.map((s, i) => (
                <div key={s.title} className="aiClerk-formV2__step">
                  <div className="aiClerk-formV2__step-head">
                    <span className="aiClerk-formV2__step-num">{i + 1}</span>
                    {i < STEPS_V2.length - 1 && (
                      <span className="aiClerk-formV2__step-line" />
                    )}
                  </div>
                  <div className="aiClerk-formV2__step-title">{s.title}</div>
                  <div className="aiClerk-formV2__step-desc">{s.desc}</div>
                </div>
              ))}
            </div>
          </section>
        </>
      ) : null}

      <div className="aiClerk-formV2__tail" />

      {/* ── 上传模板后弹窗(与旧 Home 一致) ── */}
      <Modal
        title={null}
        open={startModalOpen}
        onCancel={handleStartModalClose}
        mask={{ closable: !uploading }}
        closable={!uploading}
        footer={null}
        width={720}
        destroyOnHidden
        zIndex={790}
        wrapClassName="aiClerk-home__start-modal-wrap"
      >
        <div className="aiClerk-home__start-modal-head">
          <h2 className="aiClerk-home__start-modal-h2">上传文档</h2>
        </div>

        <div className="aiClerk-home__start-modal-block">
          <div className="aiClerk-home__start-modal-block-label">上传文档</div>
          {templateFile ? (
            <div className="aiClerk-home__start-modal-file-card">
              <div className="aiClerk-home__start-modal-file-icon-wrap">
                <FileTypeIcon
                  name={
                    /\.(docx|doc)$/i.test(templateFile.name)
                      ? 'file-word'
                      : /\.(pptx|ppt)$/i.test(templateFile.name)
                        ? 'file-ppt'
                        : 'file-excel'
                  }
                  className="aiClerk-home__start-modal-file-icon"
                />
              </div>
              <div className="aiClerk-home__start-modal-file-info">
                <div
                  className="aiClerk-home__start-modal-file-name"
                  title={templateFile.name}
                >
                  {templateFile.name}
                </div>
                <div className="aiClerk-home__start-modal-file-meta">
                  <span>{(templateFile.size / 1024).toFixed(0)} KB</span>
                </div>
              </div>
              <button
                type="button"
                className="aiClerk-home__start-modal-file-close"
                onClick={handleClearTemplateInModal}
                aria-label="移除模板"
                disabled={uploading}
              >
                ×
              </button>
            </div>
          ) : (
            <div className="aiClerk-home__start-modal-sample-card aiClerk-home__start-modal-sample-card--empty">
              <span className="aiClerk-home__start-modal-sample-icon">
                <ArrowUpOutlined />
              </span>
              <div className="aiClerk-home__start-modal-sample-text">
                <div className="aiClerk-home__start-modal-sample-title">
                  上传一份要填的文档
                </div>
                <div className="aiClerk-home__start-modal-sample-desc">
                  支持{' '}
                  <span className="aiClerk-home__start-modal-ext">.docx</span>{' '}
                  <span className="aiClerk-home__start-modal-ext">.xlsx</span>{' '}
                  格式
                </div>
              </div>
              <Button
                type="primary"
                className="aiClerk-home__start-modal-sample-btn"
                onClick={handlePickTemplate}
                disabled={uploading}
              >
                上传文档
              </Button>
            </div>
          )}
        </div>

        <div className="aiClerk-home__start-modal-block">
          <div className="aiClerk-home__start-modal-block-label">
            也可上传
            <strong className="aiClerk-home__start-modal-block-label-em">
              【样例】
            </strong>
            提高填写准确率
            <span className="aiClerk-home__start-modal-block-label-tag">
              可选
            </span>
          </div>

          {sampleFile ? (
            <div
              className={`aiClerk-home__start-modal-sample-card aiClerk-home__start-modal-sample-card--filled${
                dragOverSample ? ' is-drag-over' : ''
              }`}
              onDragOver={handleSampleDragOver}
              onDragLeave={handleSampleDragLeave}
              onDrop={handleSampleDrop}
            >
              <div className="aiClerk-home__start-modal-file-icon-wrap">
                <FileTypeIcon
                  name={
                    /\.(docx|doc)$/i.test(sampleFile.name)
                      ? 'file-word'
                      : 'file-excel'
                  }
                  className="aiClerk-home__start-modal-file-icon"
                />
              </div>
              <div className="aiClerk-home__start-modal-file-info">
                <div
                  className="aiClerk-home__start-modal-file-name"
                  title={sampleFile.name}
                >
                  {sampleFile.name}
                </div>
                <div className="aiClerk-home__start-modal-file-meta">
                  {(sampleFile.size / 1024).toFixed(0)} KB
                </div>
              </div>
              <Button
                type="primary"
                className="aiClerk-home__start-modal-sample-btn"
                onClick={handlePickSample}
                disabled={uploading}
              >
                上传样例
              </Button>
            </div>
          ) : (
            <div
              className={`aiClerk-home__start-modal-sample-card aiClerk-home__start-modal-sample-card--empty${
                dragOverSample ? ' is-drag-over' : ''
              }`}
              onDragOver={handleSampleDragOver}
              onDragLeave={handleSampleDragLeave}
              onDrop={handleSampleDrop}
            >
              <span className="aiClerk-home__start-modal-sample-icon">
                <ThunderboltFilled />
              </span>
              <div className="aiClerk-home__start-modal-sample-text">
                <div className="aiClerk-home__start-modal-sample-title">
                  上传样例,让识别更准
                </div>
                <div className="aiClerk-home__start-modal-sample-desc">
                  再传一份「之前填好的」样例,AI 会对比学习你的填写习惯,
                  提高填写正确率
                </div>
              </div>
              <Button
                type="primary"
                className="aiClerk-home__start-modal-sample-btn"
                onClick={handlePickSample}
                disabled={uploading}
              >
                上传样例
              </Button>
            </div>
          )}
        </div>

        {(probing || probeResult) && (
          <div className="aiClerk-home__probe" style={{ marginTop: 12 }}>
            {probing && (
              <div className="aiClerk-home__probe-pill aiClerk-home__probe-pill--neutral">
                <LoadingOutlined /> 正在检测样例与模板的字段匹配…
              </div>
            )}
            {!probing && probeResult && !probeResult.parseError && (
              <>
                {probeResult.matchPercent >= 70 && (
                  <div className="aiClerk-home__probe-pill aiClerk-home__probe-pill--ok">
                    <CheckCircleOutlined /> 字段匹配良好(
                    {probeResult.matchedHeaders}/{probeResult.totalHeaders}{' '}
                    列同名)
                  </div>
                )}
                {probeResult.matchPercent >= 30 &&
                  probeResult.matchPercent < 70 && (
                    <div className="aiClerk-home__probe-pill aiClerk-home__probe-pill--warn">
                      <WarningOutlined /> 仅 {probeResult.matchPercent}%
                      列对得上(
                      {probeResult.matchedHeaders}/{probeResult.totalHeaders}
                      ),确认是同类型表?
                    </div>
                  )}
                {probeResult.matchPercent < 30 &&
                  probeResult.totalHeaders > 0 && (
                    <div className="aiClerk-home__probe-pill aiClerk-home__probe-pill--err">
                      <CloseCircleOutlined /> 看起来不是同类型表(
                      {probeResult.matchedHeaders}/{probeResult.totalHeaders}{' '}
                      列匹配)
                    </div>
                  )}
                {probeResult.reversed && (
                  <div className="aiClerk-home__probe-pill aiClerk-home__probe-pill--info">
                    <SwapOutlined /> 模板槽里的文件填得更满,是不是放反了?
                    <button
                      type="button"
                      className="aiClerk-home__probe-swap"
                      onClick={handleSwapTemplateAndSample}
                      disabled={uploading}
                    >
                      一键互换
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <input
          ref={sampleInputRef}
          type="file"
          accept={ACCEPT_TYPES}
          hidden
          onChange={handleSampleChange}
        />

        <div className="aiClerk-home__start-modal-footer">
          <Button
            type="primary"
            size="large"
            className="aiClerk-home__start-btn aiClerk-home__start-modal-cta"
            onClick={() => handleStart()}
            disabled={!templateFile || uploading}
            loading={uploading}
          >
            {uploading ? '上传中…' : '开始识别'}
          </Button>
        </div>
      </Modal>

      {/* ── 积分不足拦截弹窗 + 增购积分包 ── */}
      <CreditsExhaustedModal
        visible={creditsModalVisible}
        onClose={() => setCreditsModalVisible(false)}
        available={credits?.available ?? 0}
        cost={SINGLE_QUERY_COST}
        vipKind={vipKind}
        onBuyPack={() => {
          setCreditsModalVisible(false)
          setStartModalOpen(false)
          setPointsPackVisible(true)
        }}
        onUpgrade={() => {
          setCreditsModalVisible(false)
          setStartModalOpen(false)
          payment.openVipByKind(
            vipKind || CONFIG.VIP_KIND.VIP,
            'aiFormFiller',
            false,
            aiClerkPayContent({
              isVip: vipKind > 0,
              vipKind,
              usedPoints: credits?.consumed ?? 0
            })
          )
        }}
      />
      <PointsPackModal
        visible={pointsPackVisible}
        onClose={() => setPointsPackVisible(false)}
        source="aiFormFillerBuyCredits"
        onPaymentSuccess={() => {
          setPointsPackVisible(false)
          refreshCredits()
          runPendingStart()
        }}
      />
      <EditSkillModal
        skill={editingSkill}
        open={!!editingSkill}
        onClose={() => setEditingSkill(null)}
        onSaved={() => {
          setEditingSkill(null)
          loadSkills()
        }}
      />
      <DemoReplayModal
        visible={demoVisible}
        onClose={() => setDemoVisible(false)}
      />
    </div>
  )
}

export default HomeV2
